import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AdjustmentModal from "./adjustment-modal";
import "../add-order.css";
import { splitCamelCaseToWords } from "../../../../utils/Formatter";

interface RushFee {
  id: string;
  type: string;
  label: string;
  clientFee: number;
  inspectorFee: number;
}

interface FeeRow {
  id: string;
  date: string;
  type: string;
  adjustment: string;
  amount: number;
  note: string;
  category: string;
}

type Props = {
  data: RushFee[];
  formik: any;
  formDetails: any;
  adjustmentTypes: any;
};

const Fees = (props: Props) => {
  const { data, formik, formDetails, adjustmentTypes } = props;
  const [clientFees, setClientFees] = useState<FeeRow[]>([]);
  const [inspectorFees, setInspectorFees] = useState<FeeRow[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [modalType, setModalType] = useState<"client" | "inspector">("client");
  const [editData, setEditData] = useState<FeeRow | undefined>();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteData, setDeleteData] = useState<FeeRow | undefined>();
  const [feesInitialized, setFeesInitialized] = useState(false);

  useEffect(() => {
    if (
      formDetails?.clientFee !== undefined &&
      formDetails?.inspectorFee !== undefined
    ) {
      const formRowId = "FormRow";

      setClientFees((prevClientFees) => {
        const existingFormRowIndex = prevClientFees.findIndex(
          (f) => f.id === formRowId
        );

        if (existingFormRowIndex >= 0) {
          const updatedFees = [...prevClientFees];
          updatedFees[existingFormRowIndex] = {
            ...updatedFees[existingFormRowIndex],
            amount: formDetails.clientFee,
          };
          return updatedFees;
        } else {
          return [
            {
              id: formRowId,
              date: new Date().toISOString().split("T")[0],
              type: "Billing",
              adjustment: "n/a",
              amount: formDetails.clientFee,
              note: "",
              category: "ClientFee",
            },
            ...prevClientFees,
          ];
        }
      });

      setInspectorFees((prevInspectorFees) => {
        const existingFormRowIndex = prevInspectorFees.findIndex(
          (f) => f.id === formRowId
        );

        if (existingFormRowIndex >= 0) {
          const updatedFees = [...prevInspectorFees];
          updatedFees[existingFormRowIndex] = {
            ...updatedFees[existingFormRowIndex],
            amount: formDetails.inspectorFee,
          };
          return updatedFees;
        } else {
          return [
            {
              id: formRowId,
              date: new Date().toISOString().split("T")[0],
              type: "Cost",
              adjustment: "n/a",
              amount: formDetails.inspectorFee,
              note: "",
              category: "InspectorFee",
            },
            ...prevInspectorFees,
          ];
        }
      });
    }
  }, [formDetails?.clientFee, formDetails?.inspectorFee]);

  useEffect(() => {
    const selectedRushFeeId = formik.values.rushFeeId;
    const rushFeeRowId = "RushFeeIDRow";

    if (selectedRushFeeId && data) {
      const selectedRushFee = data.find(
        (rushFee: RushFee) => rushFee.id === selectedRushFeeId
      );
      if (selectedRushFee) {
        setClientFees((prevClientFees) => {
          const existingRushFeeIndex = prevClientFees.findIndex(
            (f) => f.id === rushFeeRowId
          );

          const newRushFee = {
            id: rushFeeRowId,
            date: new Date().toISOString().split("T")[0],
            type: "PreBillingAdjustment",
            adjustment: "C_PR_RushFee",
            amount: selectedRushFee.clientFee,
            note: "",
            category: "ClientFee",
          };

          if (existingRushFeeIndex >= 0) {
            const updatedFees = [...prevClientFees];
            updatedFees[existingRushFeeIndex] = newRushFee;
            return updatedFees;
          } else {
            return [...prevClientFees, newRushFee];
          }
        });

        setInspectorFees((prevInspectorFees) => {
          const existingRushFeeIndex = prevInspectorFees.findIndex(
            (f) => f.id === rushFeeRowId
          );

          const newRushFee = {
            id: rushFeeRowId,
            date: new Date().toISOString().split("T")[0],
            type: "PreBillingAdjustment",
            adjustment: "I_PR_RushFee",
            amount: selectedRushFee.inspectorFee,
            note: "",
            category: "InspectorFee",
          };

          if (existingRushFeeIndex >= 0) {
            const updatedFees = [...prevInspectorFees];
            updatedFees[existingRushFeeIndex] = newRushFee;
            return updatedFees;
          } else {
            return [...prevInspectorFees, newRushFee];
          }
        });
      }
    } else {
      setClientFees((prevClientFees) =>
        prevClientFees.filter((f) => f.id !== rushFeeRowId)
      );
      setInspectorFees((prevInspectorFees) =>
        prevInspectorFees.filter((f) => f.id !== rushFeeRowId)
      );
    }
  }, [formik.values.rushFeeId, data]);

  useEffect(() => {
    const allFees = [
      ...clientFees.map((fee) => ({
        category: fee.category,
        type: fee.type,
        adjustmentType: fee.adjustment === "n/a" ? null : fee.adjustment,
        amount: fee.amount,
        note: fee.note,
        date: fee.date,
      })),
      ...inspectorFees.map((fee) => ({
        category: fee.category,
        type: fee.type,
        adjustmentType: fee.adjustment === "n/a" ? null : fee.adjustment,
        amount: fee.amount,
        note: fee.note,
        date: fee.date,
      })),
    ];

    const currentFees = formik.values.fees || [];
    const feesChanged = JSON.stringify(allFees) !== JSON.stringify(currentFees);

    if (feesChanged) {
      formik.setFieldValue("fees", allFees);
    }
  }, [clientFees, inspectorFees]); // eslint-disable-line

  // Reset initialization flags when fees are cleared
  useEffect(() => {
    if (
      formik.values.fees &&
      formik.values.fees.length === 0 &&
      feesInitialized
    ) {
      setFeesInitialized(false);
    }
  }, [formik.values.fees, feesInitialized]); // eslint-disable-line

  // Initialize fees from formik.values.fees when they exist (for re-order)
  useEffect(() => {
    if (
      !feesInitialized &&
      formik.values.fees &&
      formik.values.fees.length > 0
    ) {
      const existingClientFees = formik.values.fees
        .filter((fee: any) => fee.category === "ClientFee")
        .map((fee: any, index: number) => {
          // Check if it's a form fee (no adjustmentType) or rush fee
          if (fee.adjustmentType === null) {
            return {
              id: "FormRow",
              date: fee.date || new Date().toISOString().split("T")[0],
              type: fee.type || "Billing",
              adjustment: "n/a",
              amount: fee.amount,
              note: fee.note || "",
              category: "ClientFee",
            };
          } else if (fee.adjustmentType === "RushFee") {
            return {
              id: "RushFeeIDRow",
              date: fee.date || new Date().toISOString().split("T")[0],
              type: fee.type || "PreBillingAdjustment",
              adjustment: fee.adjustmentType,
              amount: fee.amount,
              note: fee.note || "",
              category: "ClientFee",
            };
          } else {
            // Regular adjustment
            return {
              id: `client-adjustment-${index}-${Date.now()}`,
              date: fee.date || new Date().toISOString().split("T")[0],
              type: fee.type || "",
              adjustment: fee.adjustmentType,
              amount: fee.amount,
              note: fee.note || "",
              category: "ClientFee",
            };
          }
        });

      const existingInspectorFees = formik.values.fees
        .filter((fee: any) => fee.category === "InspectorFee")
        .map((fee: any, index: number) => {
          // Check if it's a form fee (no adjustmentType) or rush fee
          if (fee.adjustmentType === null) {
            return {
              id: "FormRow",
              date: fee.date || new Date().toISOString().split("T")[0],
              type: fee.type || "Cost",
              adjustment: "n/a",
              amount: fee.amount,
              note: fee.note || "",
              category: "InspectorFee",
            };
          } else if (fee.adjustmentType === "RushFee") {
            return {
              id: "RushFeeIDRow",
              date: fee.date || new Date().toISOString().split("T")[0],
              type: fee.type || "PreBillingAdjustment",
              adjustment: fee.adjustmentType,
              amount: fee.amount,
              note: fee.note || "",
              category: "InspectorFee",
            };
          } else {
            // Regular adjustment
            return {
              id: `inspector-adjustment-${index}-${Date.now()}`,
              date: fee.date || new Date().toISOString().split("T")[0],
              type: fee.type || "",
              adjustment: fee.adjustmentType,
              amount: fee.amount,
              note: fee.note || "",
              category: "InspectorFee",
            };
          }
        });

      setClientFees(existingClientFees);
      setInspectorFees(existingInspectorFees);
      setFeesInitialized(true);
    }
  }, [formik.values.fees, feesInitialized]); // eslint-disable-line

  // Clear fees when form is reset
  useEffect(() => {
    if (formik.values.fees && formik.values.fees.length === 0) {
      setClientFees([]);
      setInspectorFees([]);
    }
  }, [formik.values.fees]); // eslint-disable-line

  const clientSubtotal = clientFees.reduce(
    (total, fee) => total + (parseFloat(fee.amount as any) || 0),
    0
  );
  const inspectorSubtotal = inspectorFees.reduce(
    (total, fee) => total + (parseFloat(fee.amount as any) || 0),
    0
  );

  const openAddModal = (type: "client" | "inspector") => {
    setModalType(type);
    setModalMode("add");
    setEditData(undefined);
    setIsModalOpen(true);
  };

  const openEditModal = (type: "client" | "inspector", data: FeeRow) => {
    setModalType(type);
    setModalMode("edit");
    setEditData(data);
    setIsModalOpen(true);
  };

  const handleSaveAdjustment = (data: FeeRow) => {
    if (modalType === "client") {
      if (modalMode === "edit") {
        setClientFees((prev) =>
          prev.map((fee) => (fee.id === data.id ? data : fee))
        );
      } else {
        const newData = {
          ...data,
          id: `client-adjustment-${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`,
        };
        setClientFees((prev) => [...prev, newData]);
      }
    } else {
      if (modalMode === "edit") {
        setInspectorFees((prev) =>
          prev.map((fee) => (fee.id === data.id ? data : fee))
        );
      } else {
        const newData = {
          ...data,
          id: `inspector-adjustment-${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`,
        };
        setInspectorFees((prev) => [...prev, newData]);
      }
    }
  };

  const handleDeleteAdjustment = (id: string) => {
    if (modalType === "client") {
      setClientFees((prev) => prev.filter((fee) => fee.id !== id));
    } else {
      setInspectorFees((prev) => prev.filter((fee) => fee.id !== id));
    }
  };

  const handleDeleteClientRow = (id: string) => {
    const feeToDelete = clientFees.find((fee) => fee.id === id);
    if (feeToDelete) {
      setDeleteData(feeToDelete);
      setModalType("client");
      setIsDeleteModalOpen(true);
    }
  };

  const handleDeleteInspectorRow = (id: string) => {
    const feeToDelete = inspectorFees.find((fee) => fee.id === id);
    if (feeToDelete) {
      setDeleteData(feeToDelete);
      setModalType("inspector");
      setIsDeleteModalOpen(true);
    }
  };

  const confirmDeleteFee = () => {
    if (deleteData) {
      if (modalType === "client") {
        setClientFees((prev) => prev.filter((fee) => fee.id !== deleteData.id));
      } else {
        setInspectorFees((prev) =>
          prev.filter((fee) => fee.id !== deleteData.id)
        );
      }
      setIsDeleteModalOpen(false);
      setDeleteData(undefined);
    }
  };

  const getAdjustmentDisplayName = (adjustmentType: string) => {
    if (!adjustmentType) return adjustmentType;

    const match = adjustmentTypes?.find(
      (a: { adjustmentType: string; displayName: string }) =>
        a.adjustmentType === adjustmentType
    );
    return match ? match.displayName : splitCamelCaseToWords(adjustmentType);
  };

  const cardSx = {
    p: 2,
    mb: 2,
    backgroundColor: "#FAFAFA",
    border: "1px solid #E0E0E0",
    borderRadius: "8px",
  };

  return (
    <Box sx={cardSx}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 400, mb: 2, fontSize: "24px" }}
      >
        Fees
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Client
          </Typography>
          <Button
            variant="text"
            startIcon={<AddOutlinedIcon />}
            onClick={() => openAddModal("client")}
            sx={{
              textTransform: "none",
              color: "error.main",
              fontWeight: 500,
            }}
            disabled={!formik.values.clientId}
          >
            ADD CLIENT ADJUSTMENT
          </Button>
        </Box>

        <TableContainer
          component={Paper}
          sx={{ boxShadow: "none", border: "none" }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Type</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Adjustment</TableCell>
                <TableCell sx={{ fontWeight: "bold", textAlign: "right" }}>
                  Amount
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Note</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}> </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clientFees.map((fee, index) => (
                <TableRow key={fee.id}>
                  <TableCell>
                    {new Date(fee.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </TableCell>
                  <TableCell>
                    {fee.type === "PreBillingAdjustment"
                      ? "Pre-Billing Adjustment"
                      : splitCamelCaseToWords(fee.type)}
                  </TableCell>
                  <TableCell>
                    {fee.adjustment && getAdjustmentDisplayName(fee.adjustment)}
                  </TableCell>
                  <TableCell sx={{ textAlign: "right" }}>
                    ${parseFloat(fee.amount as any)?.toFixed(2) || "0.00"}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>{fee.note}</TableCell>
                  <TableCell sx={{ width: 100 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClientRow(fee.id)}
                      sx={{
                        p: 0.5,
                        mr: 0.5,
                        visibility: fee.id !== "FormRow" ? "visible" : "hidden",
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" color="error" />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{ p: 0.5, color: "primary.main" }}
                      onClick={() => openEditModal("client", fee)}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ borderBottom: "none" }}>
                <TableCell sx={{ borderBottom: "none" }}></TableCell>
                <TableCell sx={{ borderBottom: "none" }}></TableCell>
                <TableCell sx={{ borderBottom: "none" }}></TableCell>
                <TableCell
                  sx={{
                    textAlign: "right",
                    fontWeight: "bold",
                    borderBottom: "none",
                  }}
                >
                  ${clientSubtotal.toFixed(2)}
                </TableCell>
                <TableCell sx={{ borderBottom: "none" }}></TableCell>
                <TableCell sx={{ borderBottom: "none" }}></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Inspector
          </Typography>
          <Button
            variant="text"
            startIcon={<AddOutlinedIcon />}
            onClick={() => openAddModal("inspector")}
            sx={{
              textTransform: "none",
              color: "error.main",
              fontWeight: 500,
            }}
            disabled={!formik.values.clientId}
          >
            ADD INSPECTOR ADJUSTMENT
          </Button>
        </Box>

        <TableContainer
          component={Paper}
          sx={{ boxShadow: "none", border: "none" }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Type</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Adjustment</TableCell>
                <TableCell sx={{ fontWeight: "bold", textAlign: "right" }}>
                  Amount
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Note</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inspectorFees.map((fee, index) => (
                <TableRow key={fee.id}>
                  <TableCell>
                    {new Date(fee.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </TableCell>
                  <TableCell>
                    {fee.type === "PreBillingAdjustment"
                      ? "Pre-Billing Adjustment"
                      : splitCamelCaseToWords(fee.type)}
                  </TableCell>
                  <TableCell>
                    {fee.adjustment && getAdjustmentDisplayName(fee.adjustment)}
                  </TableCell>
                  <TableCell sx={{ textAlign: "right" }}>
                    ${parseFloat(fee.amount as any)?.toFixed(2) || "0.00"}
                  </TableCell>
                  <TableCell sx={{ maxWidth: "400" }}>{fee.note}</TableCell>
                  <TableCell sx={{ width: 100 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteInspectorRow(fee.id)}
                      sx={{
                        p: 0.5,
                        mr: 0.5,
                        visibility: fee.id !== "FormRow" ? "visible" : "hidden",
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" color="error" />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{ p: 0.5, color: "primary.main" }}
                      onClick={() => openEditModal("inspector", fee)}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ borderBottom: "none" }}>
                <TableCell sx={{ borderBottom: "none" }}></TableCell>
                <TableCell sx={{ borderBottom: "none" }}></TableCell>
                <TableCell sx={{ borderBottom: "none" }}></TableCell>
                <TableCell
                  sx={{
                    textAlign: "right",
                    fontWeight: "bold",
                    borderBottom: "none",
                  }}
                >
                  ${inspectorSubtotal.toFixed(2)}
                </TableCell>
                <TableCell sx={{ borderBottom: "none" }}></TableCell>
                <TableCell sx={{ borderBottom: "none" }}></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box>
        <Box sx={{ display: "flex", gap: 3 }}>
          <Card
            sx={{
              flex: 1,
              border: "1px solid #C4C4C4",
              borderRadius: "8px",
              boxShadow: "none",
              height: "fit-content",
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: "600", fontSize: "16px" }}
              >
                Client
              </Typography>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography>Billed</Typography>
                <Typography fontSize={14}>
                  $
                  {clientFees.length > 0
                    ? (parseFloat(clientFees[0].amount as any) || 0).toFixed(2)
                    : "0.00"}
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography>Adjustments</Typography>
                <Typography fontSize={14}>
                  $
                  {clientFees.length > 1
                    ? clientFees
                        .slice(1)
                        .reduce(
                          (total, fee) =>
                            total + (parseFloat(fee.amount as any) || 0),
                          0
                        )
                        .toFixed(2)
                    : "0.00"}
                </Typography>
              </Box>
              <Divider />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                  mt: 1,
                }}
              >
                <Typography sx={{ fontWeight: "600", fontSize: "14px" }}>
                  Adjusted Total
                </Typography>
                <Typography sx={{ fontWeight: "600", fontSize: "14px" }}>
                  ${clientSubtotal.toFixed(2)}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Card
            sx={{
              flex: 1,
              border: "1px solid #C4C4C4",
              borderRadius: "8px",
              boxShadow: "none",
              height: "fit-content",
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: "600", fontSize: "16px" }}
              >
                Inspector
              </Typography>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography>Cost</Typography>
                <Typography fontSize={14}>
                  $
                  {inspectorFees.length > 0
                    ? (parseFloat(inspectorFees[0].amount as any) || 0).toFixed(
                        2
                      )
                    : "0.00"}
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography>Adjustments</Typography>
                <Typography fontSize={14}>
                  $
                  {inspectorFees.length > 1
                    ? inspectorFees
                        .slice(1)
                        .reduce(
                          (total, fee) =>
                            total + (parseFloat(fee.amount as any) || 0),
                          0
                        )
                        .toFixed(2)
                    : "0.00"}
                </Typography>
              </Box>
              <Divider />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                  mt: 1,
                }}
              >
                <Typography sx={{ fontWeight: "600", fontSize: "14px" }}>
                  Adjusted Total
                </Typography>
                <Typography sx={{ fontWeight: "600", fontSize: "14px" }}>
                  ${inspectorSubtotal.toFixed(2)}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Card
            sx={{
              flex: 1,
              border: "1px solid #C4C4C4",
              borderRadius: "8px",
              boxShadow: "none",
              height: "fit-content",
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: "600", fontSize: "16px" }}
              >
                Spectrum
              </Typography>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography>Margin</Typography>
                <Typography>
                  {clientFees.length > 0 && inspectorFees.length > 0
                    ? (
                        ((parseFloat(clientFees[0].amount as any) -
                          parseFloat(inspectorFees[0].amount as any)) /
                          parseFloat(clientFees[0].amount as any)) *
                        100
                      ).toFixed(1)
                    : 0}
                  %
                </Typography>
              </Box>
              <Divider />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                  mt: 1,
                }}
              >
                <Typography sx={{ fontWeight: "600", fontSize: "14px" }}>
                  Adjusted Margin
                </Typography>
                <Typography sx={{ fontWeight: "bold" }}>
                  {clientSubtotal > 0
                    ? (
                        ((clientSubtotal - inspectorSubtotal) /
                          clientSubtotal) *
                        100
                      ).toFixed(1)
                    : 0}
                  %
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
      <AdjustmentModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveAdjustment}
        onDelete={modalMode === "edit" ? handleDeleteAdjustment : undefined}
        mode={modalMode}
        adjustmentType={modalType}
        editData={editData}
        adjustmentOptions={adjustmentTypes || []}
      />

      <Dialog
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Delete Additional Information?</Typography>
            <IconButton
              onClick={() => {
                setIsDeleteModalOpen(false);
                setDeleteData(undefined);
              }}
            >
              <Typography>×</Typography>
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            Are you sure you want to delete this{" "}
            {modalType === "client" ? "client" : "inspector"} fee?
            <br />
            This action can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="contained"
            onClick={() => {
              setIsDeleteModalOpen(false);
              setDeleteData(undefined);
            }}
            sx={{
              backgroundColor: "primary.main",
              color: "white",
              textTransform: "none",
              "&:hover": { backgroundColor: "primary.main" },
            }}
          >
            CANCEL
          </Button>
          <Button
            onClick={confirmDeleteFee}
            variant="text"
            sx={{
              color: "error.main",
              textTransform: "none",
            }}
          >
            DELETE
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Fees;
